import { isSameDay, isWeekend, startOfDay, addDays, getYear, getDate, getMonth, getDay } from 'date-fns';

// Public Holidays (Jours fériés)
const FIXED_HOLIDAYS = [
    { month: 0, day: 1, name: "Jour de l'An" },
    { month: 4, day: 1, name: "Fête du Travail" },
    { month: 4, day: 8, name: "Victoire 1945" },
    { month: 6, day: 14, name: "Fête Nationale" },
    { month: 7, day: 15, name: "Assomption" },
    { month: 10, day: 1, name: "Toussaint" },
    { month: 10, day: 11, name: "Armistice 1918" },
    { month: 11, day: 25, name: "Noël" },
];

// Computus algorithm for Easter Date (Meeus/Jones/Butcher)
const getEasterDate = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1; // 0-indexed month
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(year, month, day);
};

const getVariableHolidays = (year: number) => {
    const easter = getEasterDate(year);
    const easterMonday = addDays(easter, 1);
    const ascension = addDays(easter, 39);
    const pentecostMonday = addDays(easter, 50);

    return [
        { date: easterMonday, name: "Lundi de Pâques" },
        { date: ascension, name: "Ascension" },
        { date: pentecostMonday, name: "Lundi de Pentecôte" },
    ];
};

// ALGORITHMIC APPROXIMATION for Zone C School Holidays (2001-2050)
// Note: Future school holidays are not officially set. This uses a consistent pattern for Zone C (Paris/Versailles/Créteil/Montpellier/Toulouse).
// - Winter: ~Mid Feb to Early March
// - Spring: ~Mid April to Early May
// - Summer: ~Early July to Early Sept
// - Toussaint: ~Late Oct to Early Nov
// - Christmas: ~Late Dec to Early Jan
const getEstimatedSchoolHolidays = (year: number) => {
    const holidays = [];

    // 1. Winter Holidays (Hiver) - Approx: Start 3rd Saturday of Feb, 2 weeks long
    // Finding 3rd Saturday of Feb
    let feb1 = new Date(year, 1, 1);
    let dayOfWeek = feb1.getDay(); // 0 is Sunday
    let daysToFirstSat = (6 - dayOfWeek + 7) % 7;
    let firstSatFeb = addDays(feb1, daysToFirstSat);
    let thirdSatFeb = addDays(firstSatFeb, 14);
    // Zone C often shifts, but let's anchor to this for consistency in long range
    // Let's vary it slightly to match 2024/2025/2026 data roughly if needed, or keep it simple.
    // In 2026, Zone C is Feb 21 (3rd Sat). In 2025, Feb 15 (3rd Sat). In 2024 (Feb 10 for Zone C? No, depends on Zone).
    // Using a fixed rule: Start 3rd Saturday of February.
    holidays.push({
        start: thirdSatFeb,
        end: addDays(thirdSatFeb, 16), // usually 2 weeks + return Monday
        name: "Vacances d'hiver"
    });

    // 2. Spring Holidays (Printemps) - Approx: Start 2nd Saturday of April
    let apr1 = new Date(year, 3, 1);
    dayOfWeek = apr1.getDay();
    daysToFirstSat = (6 - dayOfWeek + 7) % 7;
    let firstSatApr = addDays(apr1, daysToFirstSat);
    let secondSatApr = addDays(firstSatApr, 7);
    holidays.push({
        start: secondSatApr,
        end: addDays(secondSatApr, 16),
        name: "Vacances de printemps"
    });

    // 3. Summer Holidays (Grandes Vacances) - Fixed: July 7th to Sept 1st approx
    // Often starts after the first week of July. Let's say July 7.
    // Return to school: First Monday of Sept or Sept 1st.
    let sept1 = new Date(year, 8, 1);
    let returnDay = sept1.getDay() === 0 ? addDays(sept1, 1) : sept1; // If Sunday, Monday 2nd. If Sat, Monday 3rd? Actually usually Sept 1-3.
    // Let's simplify: July 6 to Sept 1.
    holidays.push({
        start: new Date(year, 6, 6),
        end: new Date(year, 8, 1),
        name: "Grandes vacances"
    });

    // 4. Toussaint - Approx: Saturday closest to Oct 19 to Nov 3-4
    // Rule: Generally starts around Oct 19-22. 2 weeks.
    // Let's optimize: Start Sat before Oct 22.
    let oct22 = new Date(year, 9, 22);
    let dayOfOct22 = oct22.getDay(); // 0-6
    // If Oct 22 is Tue (2), last Sat was Oct 19.
    let startToussaint = addDays(oct22, -((dayOfOct22 + 1) % 7));
    // Actually typically starts on a Saturday.
    holidays.push({
        start: new Date(year, 9, 19), // Fixed rough start
        end: new Date(year, 10, 4),   // Fixed rough end
        name: "Vacances de la Toussaint"
    });

    // 5. Christmas (Noël) - Approx: Sat before Dec 25 to Monday after Jan 1
    let dec25 = new Date(year, 11, 25);
    let dayOfChristmas = dec25.getDay();
    // Start usually 2nd Sat before Christmas? Or just Sat before?
    // 2024: Dec 21 (Sat). Xmas is Wed.
    // 2025: Dec 20 (Sat). Xmas is Thu.
    // Rule: Start Saturday before Christmas (or on if Xmas is Sat? No usually before).
    let daysToPrevSat = (dayOfChristmas + 1) % 7;
    let startXmas = addDays(dec25, -daysToPrevSat - (daysToPrevSat === 0 ? 7 : 0));
    // Wait, if Xmas is Fri (5), prev Sat is 6 days ago.

    // Simplest: Dec 21 to Jan 6 approx.
    holidays.push({
        start: new Date(year, 11, 21),
        end: new Date(year + 1, 0, 6),
        name: "Vacances de Noël"
    });

    return holidays;
};

export const getDayInfo = (date: Date) => {
    const d = startOfDay(date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();

    // Check Range 2001-2050
    if (year < 2001 || year > 2050) return null;

    // Check Fixed Holidays
    const fixedHoliday = FIXED_HOLIDAYS.find(h => h.month === month && h.day === day);
    if (fixedHoliday) return { type: 'holiday', name: fixedHoliday.name };

    // Check Variable Holidays
    const variableHolidays = getVariableHolidays(year);
    const varHoliday = variableHolidays.find(h => isSameDay(h.date, d));
    if (varHoliday) return { type: 'holiday', name: varHoliday.name };

    // Check School Holidays
    // Note: Used 'find' which returns the first match. Since lists are chronological, it works.
    const schoolHolidays = getEstimatedSchoolHolidays(year);
    // Also check previous year's Christmas which spills into this year (Jan)
    const prevYearHolidays = getEstimatedSchoolHolidays(year - 1);

    // Check current year vacations
    let vacation = schoolHolidays.find(v => d >= v.start && d < v.end);

    // Check spilling winter vacation from previous year (Christmas)
    if (!vacation) {
        vacation = prevYearHolidays.find(v => d >= v.start && d < v.end);
    }

    if (vacation) return { type: 'vacation', name: vacation.name };

    // Check Weekend
    if (isWeekend(d)) return { type: 'weekend', name: 'Weekend' };

    return null;
};
